import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Building2,
  ArrowLeft,
  ExternalLink,
  ShieldAlert,
  ShieldCheck,
  RefreshCw,
  Database,
  Users,
  HardDrive,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Mail,
  Calendar,
  Clock,
  Activity,
  Copy,
  Check,
  PlayCircle,
  Key,
  Shield,
  Layers,
  Folder,
  Search,
  Lock,
  ChevronRight,
  Eye,
  Edit3,
} from "lucide-react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import ErrorBoundary from "../../../components/ErrorBoundary";
import { useSuperAdmin } from "../SuperAdminContext";

export default function CompanyDetailPage() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { authenticated, setupComplete, loading: authLoading } = useSuperAdmin();

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingIntegrity, setCheckingIntegrity] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    if (!authLoading) {
      if (!setupComplete) {
        navigate("/super-admin/setup", { replace: true });
      } else if (!authenticated) {
        navigate("/super-admin/login", { replace: true });
      }
    }
  }, [authLoading, setupComplete, authenticated, navigate]);

  // Filter state for members roster
  const [memberSearch, setMemberSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  // Modals
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    targetStatus: "suspended",
  });
  const [requestModal, setRequestModal] = useState({
    open: false,
    requestedRole: "viewer",
    durationHours: 1,
    reason: "",
  });
  const [busyAction, setBusyAction] = useState(false);

  const loadData = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await api.get(`/super-admin/companies/${companyId}`);
      setDetails(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load company details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [companyId]);

  const copyToClipboard = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    toast.success(`Copied ${fieldName} to clipboard`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleRunIntegrityCheck = async () => {
    if (!companyId) return;
    setCheckingIntegrity(true);
    try {
      const res = await api.post(`/super-admin/companies/${companyId}/integrity-check`);
      if (res.data?.ok) {
        toast.success(`SQLite PRAGMA Check: ${res.data.result}`);
        if (details) {
          setDetails({
            ...details,
            storage: {
              ...details.storage,
              db_integrity: res.data.result,
              is_db_ok: true,
              db_size_formatted: res.data.size_formatted,
            },
          });
        }
      } else {
        toast.error(`Integrity Check Warning: ${res.data.error || res.data.result}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Integrity check failed");
    } finally {
      setCheckingIntegrity(false);
    }
  };

  const handleToggleStatus = async () => {
    setBusyAction(true);
    try {
      const res = await api.post(`/super-admin/companies/${companyId}/status`, {
        status: confirmModal.targetStatus,
      });
      if (res.data?.ok) {
        toast.success(`Company marked as ${confirmModal.targetStatus.toUpperCase()}`);
        setConfirmModal({ open: false, targetStatus: "suspended" });
        loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setBusyAction(false);
    }
  };

  const handleCreateSupportRequest = async () => {
    if (!requestModal.reason.trim() || requestModal.reason.trim().length < 10) {
      toast.error("Please enter a reason with at least 10 characters");
      return;
    }
    setBusyAction(true);
    try {
      const res = await api.post("/super-admin/support-requests", {
        account_id: companyId,
        requested_role: requestModal.requestedRole,
        duration_hours: Number(requestModal.durationHours),
        reason: requestModal.reason.trim(),
      });
      if (res.data?.ok) {
        toast.success("Support access requested. Awaiting company owner approval.");
        setRequestModal({ open: false, requestedRole: "viewer", durationHours: 1, reason: "" });
        loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to request support access");
    } finally {
      setBusyAction(false);
    }
  };

  const handleEnterSupportSession = async (reqId) => {
    setBusyAction(true);
    try {
      const res = await api.post(`/super-admin/support-requests/${reqId}/enter`);
      if (res.data?.ok) {
        toast.success(`Entering support session for '${res.data.company_name}'`);
        window.location.href = "/";
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to enter support session");
    } finally {
      setBusyAction(false);
    }
  };

  if (loading && !details) {
    return (
      <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center text-teal-400">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-xs font-mono tracking-widest uppercase text-slate-400">
            Loading Company Profile...
          </span>
        </div>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="min-h-screen bg-[#0B0F17] p-8 text-center text-slate-300">
        <h2 className="text-xl font-bold text-white mb-2">Company Not Found</h2>
        <p className="text-sm text-slate-400 mb-4">
          The requested company ID does not exist or may have been deleted.
        </p>
        <button
          onClick={() => navigate("/super-admin?tab=companies")}
          className="px-4 py-2 bg-teal-500 text-slate-950 font-bold rounded-xl text-xs hover:bg-teal-400"
        >
          Return to Companies
        </button>
      </div>
    );
  }

  const { account, owner, roles_count, members = [], storage, portfolio, recent_activity = [], support_history = [], current_support_request } = details;
  const isSuspended = account.status === "suspended";

  // Filter members
  const filteredMembers = members.filter((m) => {
    if (roleFilter !== "all" && m.role !== roleFilter) return false;
    if (memberSearch.trim()) {
      const q = memberSearch.toLowerCase();
      const matchU = m.username?.toLowerCase().includes(q);
      const matchE = m.email?.toLowerCase().includes(q);
      return matchU || matchE;
    }
    return true;
  });

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-[#0B0F17] text-slate-100 p-6 md:p-8 space-y-6">
        {/* Top Breadcrumb & Navigation */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <button
              onClick={() => navigate("/super-admin?tab=companies")}
              className="hover:text-teal-400 flex items-center gap-1.5 transition-colors font-medium"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Companies</span>
            </button>
            <span>/</span>
            <span className="text-white font-semibold truncate max-w-xs">{account.name}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              disabled={loading}
              className="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 hover:bg-slate-800 text-slate-300 text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-teal-400" : ""}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Header Hero Banner */}
        <div className="rounded-2xl border border-slate-800/80 bg-gradient-to-b from-slate-900/80 to-slate-950 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center shrink-0 shadow-lg shadow-teal-500/5">
              <Building2 className="w-7 h-7 text-teal-400" />
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-extrabold text-white tracking-tight">
                  {account.name}
                </h1>
                {account.id === "default" && (
                  <span className="px-2 py-0.5 rounded text-[10px] font-extrabold tracking-wider bg-teal-500/15 text-teal-400 border border-teal-500/30">
                    PRIMARY COMPANY
                  </span>
                )}
                {isSuspended ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-extrabold tracking-wider uppercase bg-rose-500/15 text-rose-400 border border-rose-500/30">
                    <ShieldAlert className="w-3.5 h-3.5" /> Suspended
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-extrabold tracking-wider uppercase bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                    <ShieldCheck className="w-3.5 h-3.5" /> Active
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3 text-xs text-slate-400 mt-2 flex-wrap">
                <span className="font-mono text-slate-500">ID: {account.id}</span>
                <span>•</span>
                <span>Created: {new Date(account.created_at).toLocaleDateString()}</span>
                {account.subdomain && (
                  <>
                    <span>•</span>
                    <a
                      href={`https://${account.subdomain}.rentaltracker.io`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-400 hover:underline flex items-center gap-1 font-mono font-semibold"
                    >
                      <span>@{account.subdomain}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Support Session CTA */}
            {current_support_request?.status === "approved" ? (
              <button
                onClick={() => handleEnterSupportSession(current_support_request.id)}
                disabled={busyAction}
                className="px-4 py-2.5 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-xs flex items-center gap-2 shadow-lg shadow-teal-500/20 transition-colors disabled:opacity-50"
              >
                <PlayCircle className="w-4 h-4" />
                <span>Enter Support ({current_support_request.requested_role.toUpperCase()})</span>
              </button>
            ) : current_support_request?.status === "pending" ? (
              <div className="px-3.5 py-2 rounded-xl bg-amber-500/10 border border-amber-500/25 text-amber-300 text-xs font-medium flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-amber-400" />
                <span>Awaiting Owner Approval</span>
              </div>
            ) : (
              <button
                onClick={() => setRequestModal({ open: true, requestedRole: "viewer", durationHours: 1, reason: "" })}
                className="px-4 py-2.5 rounded-xl bg-slate-900 border border-slate-700 hover:border-teal-500/50 hover:bg-slate-800 text-slate-200 text-xs font-semibold flex items-center gap-2 transition-colors"
              >
                <Key className="w-4 h-4 text-teal-400" />
                <span>Request Support Access</span>
              </button>
            )}

            {/* Suspend / Activate Button */}
            <button
              onClick={() => setConfirmModal({ open: true, targetStatus: isSuspended ? "active" : "suspended" })}
              disabled={busyAction}
              className={`px-4 py-2.5 rounded-xl border text-xs font-semibold flex items-center gap-2 transition-colors ${
                isSuspended
                  ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20"
                  : "bg-rose-500/10 border-rose-500/30 text-rose-400 hover:bg-rose-500/20"
              }`}
            >
              {isSuspended ? (
                <>
                  <ShieldCheck className="w-4 h-4" />
                  <span>Reactivate Company</span>
                </>
              ) : (
                <>
                  <ShieldAlert className="w-4 h-4" />
                  <span>Suspend Company</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* 4 Overview Telemetry Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Card 1: SQLite Database */}
          <div className="p-5 rounded-2xl border border-slate-800 bg-slate-900/40 space-y-3">
            <div className="flex items-center justify-between text-slate-400 text-xs font-medium">
              <span className="flex items-center gap-1.5">
                <Database className="w-4 h-4 text-amber-400" />
                SQLite Database
              </span>
              <button
                onClick={handleRunIntegrityCheck}
                disabled={checkingIntegrity}
                className="text-[10px] text-amber-400 hover:text-amber-300 font-bold flex items-center gap-1"
                title="Run live PRAGMA integrity check"
              >
                <RefreshCw className={`w-3 h-3 ${checkingIntegrity ? "animate-spin" : ""}`} />
                <span>Check</span>
              </button>
            </div>
            <div className="text-2xl font-black text-white">
              {storage?.db_size_formatted || "0 KB"}
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-slate-800/80 text-[11px]">
              <span className="text-slate-500">Integrity Check</span>
              {storage?.is_db_ok ? (
                <span className="font-bold text-emerald-400 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> OK
                </span>
              ) : (
                <span className="font-bold text-amber-400 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> {storage?.db_integrity || "Unverified"}
                </span>
              )}
            </div>
          </div>

          {/* Card 2: Uploads & Attachments */}
          <div className="p-5 rounded-2xl border border-slate-800 bg-slate-900/40 space-y-3">
            <div className="flex items-center justify-between text-slate-400 text-xs font-medium">
              <span className="flex items-center gap-1.5">
                <FileText className="w-4 h-4 text-blue-400" />
                Uploads & Storage
              </span>
              <span className="text-[10px] font-mono text-slate-500">
                {storage?.uploads_count ?? 0} files
              </span>
            </div>
            <div className="text-2xl font-black text-white">
              {storage?.storage_size_formatted || "0 KB"}
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-slate-800/80 text-[11px] text-slate-400">
              <span>Images: <strong className="text-slate-200">{storage?.uploads_categories?.images || 0}</strong></span>
              <span>Docs: <strong className="text-slate-200">{storage?.uploads_categories?.documents || 0}</strong></span>
              <span>Other: <strong className="text-slate-200">{storage?.uploads_categories?.other || 0}</strong></span>
            </div>
          </div>

          {/* Card 3: Team Roster & Roles */}
          <div className="p-5 rounded-2xl border border-slate-800 bg-slate-900/40 space-y-3">
            <div className="flex items-center justify-between text-slate-400 text-xs font-medium">
              <span className="flex items-center gap-1.5">
                <Users className="w-4 h-4 text-purple-400" />
                Team Roster
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-purple-500/10 text-purple-400 border border-purple-500/20">
                {roles_count.total} members
              </span>
            </div>
            <div className="text-2xl font-black text-white">
              {roles_count.total}
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-slate-800/80 text-[11px] font-mono">
              <span className="text-amber-400">👑 {roles_count.owner || 0} Owners</span>
              <span className="text-blue-400">✏️ {roles_count.editor || 0} Editors</span>
              <span className="text-purple-400">👁️ {roles_count.viewer || 0} Viewers</span>
            </div>
          </div>

          {/* Card 4: Portfolio Entities */}
          <div className="p-5 rounded-2xl border border-slate-800 bg-slate-900/40 space-y-3">
            <div className="flex items-center justify-between text-slate-400 text-xs font-medium">
              <span className="flex items-center gap-1.5">
                <Activity className="w-4 h-4 text-emerald-400" />
                Portfolio Summary
              </span>
              {portfolio?.latest_transaction_date && (
                <span className="text-[10px] font-mono text-slate-500">
                  Last: {portfolio.latest_transaction_date}
                </span>
              )}
            </div>
            <div className="text-2xl font-black text-white">
              {portfolio?.properties_count || 0} <span className="text-sm font-semibold text-slate-500">Properties</span>
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-slate-800/80 text-[11px] text-slate-400">
              <span>Tenants: <strong className="text-slate-200">{portfolio?.tenants_count || 0}</strong></span>
              <span>Transactions: <strong className="text-slate-200">{portfolio?.transactions_count || 0}</strong></span>
            </div>
          </div>
        </div>

        {/* Primary Contact & Host Paths Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Owner Card */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5 space-y-3">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <Mail className="w-4 h-4 text-teal-400" />
                Primary Account Owner
              </span>
              {owner?.created_at && (
                <span className="text-[11px] text-slate-500 font-mono">
                  Joined: {new Date(owner.created_at).toLocaleDateString()}
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 pt-1">
              <div className="p-3.5 rounded-xl bg-slate-950/60 border border-slate-800/80">
                <div className="text-[10px] text-slate-500 uppercase font-semibold">Username</div>
                <div className="font-bold text-white text-base mt-1 truncate">
                  {owner?.username || "None registered"}
                </div>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-950/60 border border-slate-800/80">
                <div className="text-[10px] text-slate-500 uppercase font-semibold flex items-center justify-between">
                  <span>Email Address</span>
                  {owner?.email && (
                    <button
                      onClick={() => copyToClipboard(owner.email, "Owner Email")}
                      className="text-slate-400 hover:text-white"
                      title="Copy Email"
                    >
                      {copiedField === "Owner Email" ? <Check className="w-3 h-3 text-teal-400" /> : <Copy className="w-3 h-3" />}
                    </button>
                  )}
                </div>
                <div className="font-bold text-teal-400 text-base mt-1 truncate">
                  {owner?.email ? (
                    <a href={`mailto:${owner.email}`} className="hover:underline">
                      {owner.email}
                    </a>
                  ) : (
                    <span className="text-slate-500 font-normal text-sm">None recorded</span>
                  )}
                </div>
              </div>
            </div>

            {owner?.last_login && (
              <div className="text-[11px] text-slate-400 flex items-center gap-2 pt-1">
                <Clock className="w-3.5 h-3.5 text-slate-500" />
                <span>Last login: {owner.last_login} {owner.last_login_ip ? `(${owner.last_login_ip})` : ""}</span>
              </div>
            )}
          </div>

          {/* Infrastructure Disk Paths Card */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5 space-y-3">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <Folder className="w-4 h-4 text-amber-400" />
              Infrastructure & Server Paths
            </div>

            <div className="space-y-2 pt-1 text-xs font-mono">
              <div>
                <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1">
                  <span>SQLite Database File:</span>
                  <button
                    onClick={() => copyToClipboard(account.db_path, "DB Path")}
                    className="text-slate-400 hover:text-white"
                    title="Copy DB Path"
                  >
                    {copiedField === "DB Path" ? <Check className="w-3 h-3 text-teal-400" /> : <Copy className="w-3 h-3" />}
                  </button>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 text-slate-300 truncate">
                  {account.db_path}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1">
                  <span>Tenant Storage Directory:</span>
                  <button
                    onClick={() => copyToClipboard(account.storage_dir, "Storage Dir")}
                    className="text-slate-400 hover:text-white"
                    title="Copy Storage Dir"
                  >
                    {copiedField === "Storage Dir" ? <Check className="w-3 h-3 text-teal-400" /> : <Copy className="w-3 h-3" />}
                  </button>
                </div>
                <div className="p-2.5 rounded-lg bg-slate-950/80 border border-slate-800 text-slate-300 truncate">
                  {account.storage_dir}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Team Members Roster Table */}
        <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
          <div className="p-5 border-b border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-teal-400" />
                <span>Team Members Roster ({members.length})</span>
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                All user accounts authorized to access this company portfolio.
              </p>
            </div>

            {/* Filter and Search */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search user or email..."
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  className="pl-9 pr-4 py-2 bg-slate-950/80 border border-slate-800 rounded-xl text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-teal-500/50"
                />
              </div>

              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-3 py-2 bg-slate-950/80 border border-slate-800 rounded-xl text-xs text-slate-300 focus:outline-none focus:border-teal-500/50"
              >
                <option value="all">All Roles</option>
                <option value="owner">Owners</option>
                <option value="editor">Editors</option>
                <option value="viewer">Viewers</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-950/60 text-slate-400 uppercase text-[10px] tracking-wider border-b border-slate-800">
                <tr>
                  <th className="py-3 px-5">Member</th>
                  <th className="py-3 px-4">Role</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Joined Date</th>
                  <th className="py-3 px-4">Last Active</th>
                  <th className="py-3 px-4 text-right">Logins</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 text-slate-300">
                {filteredMembers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-slate-500">
                      No members match the filter criteria.
                    </td>
                  </tr>
                ) : (
                  filteredMembers.map((m) => (
                    <tr key={m.id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="py-3.5 px-5">
                        <div className="font-semibold text-white">{m.username}</div>
                        <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                          {m.email || "No email"}
                        </div>
                      </td>

                      <td className="py-3.5 px-4">
                        {m.role === "owner" ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-400 border border-amber-500/30">
                            <Shield className="w-3 h-3" /> Owner
                          </span>
                        ) : m.role === "editor" ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-500/15 text-blue-400 border border-blue-500/30">
                            <Edit3 className="w-3 h-3" /> Editor
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-purple-500/15 text-purple-400 border border-purple-500/30">
                            <Eye className="w-3 h-3" /> Viewer
                          </span>
                        )}
                      </td>

                      <td className="py-3.5 px-4">
                        {m.status === "active" ? (
                          <span className="text-emerald-400 font-semibold text-[11px]">Active</span>
                        ) : (
                          <span className="text-rose-400 font-semibold text-[11px]">{m.status}</span>
                        )}
                      </td>

                      <td className="py-3.5 px-4 text-slate-400 font-mono text-[11px]">
                        {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
                      </td>

                      <td className="py-3.5 px-4 text-slate-400 font-mono text-[11px]">
                        {m.last_login ? (
                          <span>
                            {m.last_login} {m.last_login_ip ? `(${m.last_login_ip})` : ""}
                          </span>
                        ) : (
                          <span className="text-slate-600">Never</span>
                        )}
                      </td>

                      <td className="py-3.5 px-4 text-right font-mono font-semibold text-slate-300">
                        {m.login_count || 0}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* 2-Column: Recent Audit Activity & Support History */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activity */}
          <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 p-5 space-y-4">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Activity className="w-4 h-4 text-teal-400" />
              <span>Recent Activity in Company ({recent_activity.length})</span>
            </h3>

            {recent_activity.length === 0 ? (
              <p className="text-xs text-slate-500 py-6 text-center">
                No recent activity recorded for this company.
              </p>
            ) : (
              <div className="space-y-2.5 max-h-80 overflow-y-auto pr-1">
                {recent_activity.map((act) => (
                  <div
                    key={act.id}
                    className="p-3 rounded-xl bg-slate-950/60 border border-slate-800/80 text-xs flex items-start justify-between gap-3"
                  >
                    <div>
                      <div className="font-semibold text-slate-200">
                        <strong className="text-teal-400">@{act.username || "system"}</strong>: {act.action}
                      </div>
                      {act.notes && (
                        <div className="text-[11px] text-slate-400 mt-0.5">{act.notes}</div>
                      )}
                    </div>
                    <div className="text-[10px] font-mono text-slate-500 shrink-0 text-right">
                      {act.timestamp}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Delegated Support Requests History */}
          <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 p-5 space-y-4">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Key className="w-4 h-4 text-amber-400" />
              <span>Delegated Support History ({support_history.length})</span>
            </h3>

            {support_history.length === 0 ? (
              <p className="text-xs text-slate-500 py-6 text-center">
                No support requests created for this account.
              </p>
            ) : (
              <div className="space-y-2.5 max-h-80 overflow-y-auto pr-1">
                {support_history.map((req) => (
                  <div
                    key={req.id}
                    className="p-3 rounded-xl bg-slate-950/60 border border-slate-800/80 text-xs space-y-1.5"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-white">
                        {req.requested_role?.toUpperCase()} Access ({req.duration_hours}h)
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                          req.status === "approved"
                            ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                            : req.status === "pending"
                            ? "bg-amber-500/15 text-amber-400 border border-amber-500/30"
                            : "bg-slate-800 text-slate-400"
                        }`}
                      >
                        {req.status}
                      </span>
                    </div>
                    {req.reason && (
                      <p className="text-[11px] text-slate-400 italic">
                        "{req.reason}"
                      </p>
                    )}
                    <div className="text-[10px] text-slate-500 font-mono">
                      Requested {req.requested_at}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Modal: Suspend / Reactivate Confirmation */}
        {confirmModal.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
            <div className="w-full max-w-md bg-[#0D1117] border border-slate-800 rounded-2xl p-6 space-y-4 shadow-2xl">
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    confirmModal.targetStatus === "suspended"
                      ? "bg-rose-500/15 text-rose-400 border border-rose-500/30"
                      : "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                  }`}
                >
                  {confirmModal.targetStatus === "suspended" ? (
                    <ShieldAlert className="w-5 h-5" />
                  ) : (
                    <ShieldCheck className="w-5 h-5" />
                  )}
                </div>
                <div>
                  <h4 className="text-base font-bold text-white">
                    {confirmModal.targetStatus === "suspended"
                      ? `Suspend ${account.name}?`
                      : `Reactivate ${account.name}?`}
                  </h4>
                  <p className="text-xs text-slate-400">
                    {confirmModal.targetStatus === "suspended"
                      ? "Users belonging to this company will be blocked from logging in."
                      : "Restore full portal and login access to members of this company."}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  onClick={() => setConfirmModal({ open: false, targetStatus: "suspended" })}
                  disabled={busyAction}
                  className="px-4 py-2 rounded-xl text-slate-400 hover:text-white text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleToggleStatus}
                  disabled={busyAction}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors ${
                    confirmModal.targetStatus === "suspended"
                      ? "bg-rose-600 hover:bg-rose-500 text-white"
                      : "bg-emerald-600 hover:bg-emerald-500 text-white"
                  }`}
                >
                  {busyAction ? "Updating..." : `Confirm ${confirmModal.targetStatus === "suspended" ? "Suspend" : "Activate"}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Request Support Access */}
        {requestModal.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
            <div className="w-full max-w-lg bg-[#0D1117] border border-slate-800 rounded-2xl p-6 space-y-4 shadow-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-teal-500/15 border border-teal-500/30 flex items-center justify-center text-teal-400">
                  <Key className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-base font-bold text-white">Request Support Access</h4>
                  <p className="text-xs text-slate-400">
                    Submit a delegated support request to the owner of {account.name}.
                  </p>
                </div>
              </div>

              <div className="space-y-3 pt-2 text-xs">
                <div>
                  <label className="block text-slate-400 font-semibold mb-1">Requested Role</label>
                  <select
                    value={requestModal.requestedRole}
                    onChange={(e) => setRequestModal({ ...requestModal, requestedRole: e.target.value })}
                    className="w-full p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-200"
                  >
                    <option value="viewer">Read-Only Viewer</option>
                    <option value="editor">Editor (Full Troubleshoot)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-400 font-semibold mb-1">Duration (Hours)</label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    value={requestModal.durationHours}
                    onChange={(e) => setRequestModal({ ...requestModal, durationHours: e.target.value })}
                    className="w-full p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-200"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 font-semibold mb-1">
                    Reason for Access (at least 10 chars)
                  </label>
                  <textarea
                    rows={3}
                    placeholder="e.g. Assisting owner with transaction reconciliation diagnostics..."
                    value={requestModal.reason}
                    onChange={(e) => setRequestModal({ ...requestModal, reason: e.target.value })}
                    className="w-full p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-teal-500/50"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  onClick={() => setRequestModal({ open: false, requestedRole: "viewer", durationHours: 1, reason: "" })}
                  disabled={busyAction}
                  className="px-4 py-2 rounded-xl text-slate-400 hover:text-white text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateSupportRequest}
                  disabled={busyAction}
                  className="px-4 py-2 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 text-xs font-bold transition-colors disabled:opacity-50"
                >
                  {busyAction ? "Submitting..." : "Send Access Request"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}
